import type { LocationId, RmProfileLookupId, RmReplyTemplateId, RmReportId, RmReviewId } from '@brightlocal/shared';

import type {
  RMAutoReplyRuleLocationParams,
  RMCreateAutoReplyRuleSettings,
  RMCreateProfileLookupSettings,
  RMCreateReplyTemplateSettings,
  RMCreateReportSettings,
  RMDirectoryBreakdownParams,
  RMFindAutoReplyRulesParams,
  RMFindReplyTemplatesParams,
  RMFindReviewsParams,
  RMPerformanceParams,
  RMRespondToReviewSettings,
  RMUpdateAutoReplyRuleLocationSettings,
  RMUpdateReplyTemplateSettings,
  RMUpdateReviewSettings
} from './schema';
import {
  RMAvailableDirectoriesResponseSchema,
  RMCreateAutoReplyRuleResponseSchema,
  RMCreateProfileLookupResponseSchema,
  RMCreateReplyTemplateResponseSchema,
  RMCreateReportResponseSchema,
  RMFindReplyTemplatesResponseSchema,
  RMFindReviewsResponseSchema,
  RMGeneratedReviewReplySchema,
  RMProfileLookupSchema,
  RMReplyTemplateSchema,
  RMReviewReplySchema,
  RMReportSchema,
  RMDirectoryBreakdownResponseSchema,
  RMFindAutoReplyRulesResponseSchema,
  RMPerformanceSchema,
  RMRunReportResponseSchema
} from './schema';
import { HTTP_METHOD } from '../../../consts';
import { NoContentResponseSchema } from '../../../schemas';
import type { ApiCallOptions, Transport } from '../../../types';

export const createRMReportApi = (transport: Transport) => ({
  createReport: (settings: RMCreateReportSettings, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/reports', {
      method: HTTP_METHOD.POST,
      body: settings,
      deserializer: RMCreateReportResponseSchema,
      signal: options?.signal
    }),
  getReport: (reportId: RmReportId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reports/${reportId}`, {
      method: HTTP_METHOD.GET,
      deserializer: RMReportSchema,
      signal: options?.signal
    }),
  runReport: (reportId: RmReportId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reports/${reportId}/actions/run`, {
      method: HTTP_METHOD.POST,
      deserializer: RMRunReportResponseSchema,
      signal: options?.signal
    }),
  // transport skips undefined/null keys, so unset params are omitted from the URL automatically
  getReviewPerformance: ({ reportId, from, to }: RMPerformanceParams, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/reviews/performance', {
      method: HTTP_METHOD.GET,
      deserializer: RMPerformanceSchema,
      queryParams: { report_id: reportId, from, to },
      signal: options?.signal
    }),
  getReviewDirectoryBreakdown: (
    { reportId, from, to, interval, type }: RMDirectoryBreakdownParams,
    options?: ApiCallOptions
  ) =>
    transport('/manage/v2/rm/reviews/directory-breakdown', {
      method: HTTP_METHOD.GET,
      deserializer: RMDirectoryBreakdownResponseSchema,
      queryParams: { report_id: reportId, from, to, interval, type },
      signal: options?.signal
    }),
  getAvailableDirectories: (locationId: LocationId, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/directories', {
      method: HTTP_METHOD.GET,
      deserializer: RMAvailableDirectoriesResponseSchema,
      queryParams: { location_id: locationId },
      signal: options?.signal
    }),
  createProfileLookup: (settings: RMCreateProfileLookupSettings, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/profile-lookups', {
      method: HTTP_METHOD.POST,
      body: settings,
      deserializer: RMCreateProfileLookupResponseSchema,
      signal: options?.signal
    }),
  getProfileLookup: (lookupId: RmProfileLookupId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/profile-lookups/${lookupId}`, {
      method: HTTP_METHOD.GET,
      deserializer: RMProfileLookupSchema,
      signal: options?.signal
    }),
  // `sort` goes through as given: without it the API returns newest first
  findReviews: (params: RMFindReviewsParams, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/reviews/search', {
      method: HTTP_METHOD.POST,
      body: params,
      deserializer: RMFindReviewsResponseSchema,
      signal: options?.signal
    }),
  updateReview: (reviewId: RmReviewId, settings: RMUpdateReviewSettings, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reviews/${reviewId}`, {
      method: HTTP_METHOD.PATCH,
      body: settings,
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    }),
  getReviewReply: (reviewId: RmReviewId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reviews/${reviewId}/reply`, {
      method: HTTP_METHOD.GET,
      deserializer: RMReviewReplySchema,
      signal: options?.signal
    }),
  respondToReview: (reviewId: RmReviewId, settings: RMRespondToReviewSettings, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reviews/${reviewId}/reply`, {
      method: HTTP_METHOD.POST,
      body: settings,
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    }),
  generateReviewReply: (reviewId: RmReviewId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reviews/${reviewId}/actions/generate-reply`, {
      method: HTTP_METHOD.POST,
      deserializer: RMGeneratedReviewReplySchema,
      signal: options?.signal
    }),
  createReplyTemplate: (settings: RMCreateReplyTemplateSettings, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/reply-templates', {
      method: HTTP_METHOD.POST,
      body: settings,
      deserializer: RMCreateReplyTemplateResponseSchema,
      signal: options?.signal
    }),
  findReplyTemplates: (params: RMFindReplyTemplatesParams, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/reply-templates/search', {
      method: HTTP_METHOD.POST,
      body: params,
      deserializer: RMFindReplyTemplatesResponseSchema,
      signal: options?.signal
    }),
  getReplyTemplate: (templateId: RmReplyTemplateId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reply-templates/${templateId}`, {
      method: HTTP_METHOD.GET,
      deserializer: RMReplyTemplateSchema,
      signal: options?.signal
    }),
  updateReplyTemplate: (
    templateId: RmReplyTemplateId,
    settings: RMUpdateReplyTemplateSettings,
    options?: ApiCallOptions
  ) =>
    transport(`/manage/v2/rm/reply-templates/${templateId}`, {
      method: HTTP_METHOD.PATCH,
      body: settings,
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    }),
  deleteReplyTemplate: (templateId: RmReplyTemplateId, options?: ApiCallOptions) =>
    transport(`/manage/v2/rm/reply-templates/${templateId}`, {
      method: HTTP_METHOD.DELETE,
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    }),
  findAutoReplyRules: (params: RMFindAutoReplyRulesParams, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/auto-reply-rules/search', {
      method: HTTP_METHOD.POST,
      body: params,
      deserializer: RMFindAutoReplyRulesResponseSchema,
      signal: options?.signal
    }),
  createAutoReplyRule: (settings: RMCreateAutoReplyRuleSettings, options?: ApiCallOptions) =>
    transport('/manage/v2/rm/auto-reply-rules', {
      method: HTTP_METHOD.POST,
      body: settings,
      deserializer: RMCreateAutoReplyRuleResponseSchema,
      signal: options?.signal
    }),
  updateAutoReplyRuleLocation: (
    { ruleId, locationId }: RMAutoReplyRuleLocationParams,
    settings: RMUpdateAutoReplyRuleLocationSettings,
    options?: ApiCallOptions
  ) =>
    transport(`/manage/v2/rm/auto-reply-rules/${ruleId}/locations/${locationId}`, {
      method: HTTP_METHOD.PATCH,
      body: settings,
      deserializer: NoContentResponseSchema,
      signal: options?.signal
    })
});

export type RMReportApi = ReturnType<typeof createRMReportApi>;
